import { MAX_FILE_BYTES, SearchQuery, SearchResult, defineEndpoint } from '@itc/shared';
import { RequestView } from '@itc-mod/donations/shared';
import { EntryTitle } from '@itc-mod/library/shared';
import { BattleResource, Item, Lot } from '@itc-mod/supply/shared';
import { z } from 'zod';
import { ScorePeriod } from './people.ts';
import { ChecklistAction, Crew, CrewInput, CrewTimeInput, DepositInput, EarningsInput, EventLink, HaulBatchInput, HaulInput, HaulLine, HaulUpdate, HideInput, LinkInput, LoadoutAction, LogEntry, LogInput, NotesInput, Op, OpInput, OpKind, OpSummary, OpUpdate, People, PickupInput, Presence, Profile, ProfileInput, ProfileUpdate, PublishInput, RecordId, Report, ReportKind, ReportSummary, ReportTemplate, ReportUpdate, ReportVersion, ReverseInput, ShipsAction, SopAction, StatusInput, StockHaulInput, TemplateInput, TimelineAction, UsageAction } from './schemas.ts';

// Ops' contracts (design §7.3, plan 2D). Every change takes an Idempotency-Key, so a
// retried request never adds or deducts twice. Every route has a real permission, but
// for search and a report's own routes: those two kinds of reader can each act, so
// `mayReport` decides, inside.

const ById = z.object({ id: RecordId });
const Removed = z.object({ ok: z.literal(true) });

/** An op on a list, with its crew count and haul in thousandths of an SCU. */
export const OpRow = z.object({ op: Op, crewCount: z.number().int(), haulScu: z.number().int() });
export type OpRow = z.infer<typeof OpRow>;

/** A linked entry that still exists, read through the Library's `entries` as Ops' service principal (R3). */
export const SopLink = z.object({ id: z.string(), title: z.string() });
export type SopLink = z.infer<typeof SopLink>;

/**
 * An op in full, with what hangs off it, and who it names. `left` holds crew who have
 * lost access (Assumption 15). `onHand` holds what each lot of a planned loadout
 * holds now (R8), and a lot that's gone isn't in it.
 */
export const OpDetail = z.object({ op: Op, crew: z.array(Crew), haul: z.array(HaulLine), log: z.array(LogEntry), people: People, left: z.array(z.string()), onHand: z.record(z.string(), z.number().int()), sops: z.array(SopLink), link: EventLink.nullable(), presence: z.array(Presence) });
export type OpDetail = z.infer<typeof OpDetail>;

/** R15: what the caller may find. Open to any login, as each kind has its own permission, checked inside. */
export const searchOps = defineEndpoint({ method: 'GET', path: '/ops/search', permission: 'authenticated', query: SearchQuery, response: SearchResult });

/** The upcoming ops, as members see them (Q4): summaries and nothing more. */
export const getSchedule = defineEndpoint({ method: 'GET', path: '/ops/schedule', permission: 'ops.view_schedule', response: z.object({ items: z.array(OpSummary) }) });

/** Every op, in R3's order, for the Ops board and the leaders' Schedule. */
export const listOps = defineEndpoint({
  method: 'GET',
  path: '/ops/ops',
  permission: 'ops.view',
  query: z.object({ filter: z.enum(['open', 'upcoming', 'complete', 'all']).default('all'), kind: OpKind.optional() }),
  response: z.object({ items: z.array(OpRow), people: People }),
});

export const getOp = defineEndpoint({ method: 'GET', path: '/ops/ops/:id', permission: 'ops.view', params: ById, response: OpDetail });

/** Plan 3A: makes the op's Discord event. The answer comes back on the link, and the op itself doesn't change. */
export const createEventLink = defineEndpoint({ method: 'POST', path: '/ops/ops/:id/event', permission: 'ops.schedule', params: ById, body: LinkInput, response: OpDetail, idempotencyKey: 'required' });

/** Plan 3A: lets go of the op's Discord event, which stays on Discord. */
export const clearEventLink = defineEndpoint({ method: 'DELETE', path: '/ops/ops/:id/event', permission: 'ops.schedule', params: ById, response: OpDetail, idempotencyKey: 'required' });

/** Plan 3A: sends the op's details to its Discord event again. */
export const resendEventLink = defineEndpoint({ method: 'POST', path: '/ops/ops/:id/event/resend', permission: 'ops.schedule', params: ById, response: OpDetail, idempotencyKey: 'required' });

/** The voice channels an event can use, cached a minute. 503 with no worker. */
export const getEventChannels = defineEndpoint({ method: 'GET', path: '/ops/event-channels', permission: 'ops.schedule', response: z.object({ channels: z.array(z.object({ id: z.string(), name: z.string() })) }) });

export const createOp = defineEndpoint({ method: 'POST', path: '/ops/ops', permission: 'ops.manage', body: OpInput, response: OpDetail, idempotencyKey: 'required' });

/** Edits the details, compare-and-set on the version. */
export const updateOp = defineEndpoint({ method: 'PUT', path: '/ops/ops/:id', permission: 'ops.manage', params: ById, body: OpUpdate, response: OpDetail, idempotencyKey: 'required' });

/** Any status to any other, as the panel's select. It names one field, so it takes no version. */
export const setOpStatus = defineEndpoint({ method: 'POST', path: '/ops/ops/:id/status', permission: 'ops.manage', params: ById, body: StatusInput, response: OpDetail, idempotencyKey: 'required' });

/** Refused once anything has settled. Its crew and log follow. */
export const deleteOp = defineEndpoint({ method: 'DELETE', path: '/ops/ops/:id', permission: 'ops.manage', params: ById, response: Removed, idempotencyKey: 'required' });

/** A copy a week later (R5). */
export const duplicateOp = defineEndpoint({ method: 'POST', path: '/ops/ops/:id/duplicate', permission: 'ops.manage', params: ById, response: OpDetail, idempotencyKey: 'required' });

/** The briefing, results, losses and outcome, compare-and-set on the version. */
export const setOpNotes = defineEndpoint({ method: 'PUT', path: '/ops/ops/:id/notes', permission: 'ops.manage', params: ById, body: NotesInput, response: OpDetail, idempotencyKey: 'required' });

export const changeTimeline = defineEndpoint({ method: 'POST', path: '/ops/ops/:id/timeline', permission: 'ops.manage', params: ById, body: TimelineAction, response: z.object({ op: Op }), idempotencyKey: 'required' });

export const changeSops = defineEndpoint({ method: 'POST', path: '/ops/ops/:id/sops', permission: 'ops.manage', params: ById, body: SopAction, response: z.object({ op: Op }), idempotencyKey: 'required' });

/** Every entry, for the SOP picker. */
export const getSopChoices = defineEndpoint({ method: 'GET', path: '/ops/sop-choices', permission: 'ops.manage', response: z.object({ items: z.array(EntryTitle) }) });

/** An op with what the recap reports read (R4): for `haul` and `battle` on the Reports tab. */
export const RecapRow = z.object({ op: Op, crewCount: z.number().int(), haul: z.array(HaulLine) });
export type RecapRow = z.infer<typeof RecapRow>;

export const getRecap = defineEndpoint({ method: 'GET', path: '/ops/recap', permission: 'ops.view', query: z.object({ days: z.coerce.number().int().min(1).max(365).default(30) }), response: z.object({ items: z.array(RecapRow) }) });

export const changeChecklist = defineEndpoint({ method: 'POST', path: '/ops/ops/:id/checklist', permission: 'ops.manage', params: ById, body: ChecklistAction, response: z.object({ op: Op }), idempotencyKey: 'required' });

/** Members by id, and names pasted in (R1). What's skipped says why. */
export const addCrew = defineEndpoint({
  method: 'POST',
  path: '/ops/ops/:id/crew',
  permission: 'ops.manage',
  params: ById,
  body: CrewInput,
  response: z.object({ added: z.array(Crew), skipped: z.array(z.object({ name: z.string(), reason: z.string() })), people: People }),
  idempotencyKey: 'required',
});

/** Plan 3B R6: sets a row's minutes, so settling again leaves them. It names one row, so it takes no version. */
export const setCrewTime = defineEndpoint({ method: 'PUT', path: '/ops/crew/:id/time', permission: 'ops.manage', params: ById, body: CrewTimeInput, response: Crew, idempotencyKey: 'required' });

export const removeCrew = defineEndpoint({ method: 'DELETE', path: '/ops/crew/:id', permission: 'ops.manage', params: ById, response: Removed, idempotencyKey: 'required' });

export const addLog = defineEndpoint({ method: 'POST', path: '/ops/ops/:id/log', permission: 'ops.manage', params: ById, body: LogInput, response: z.object({ entry: LogEntry }), idempotencyKey: 'required' });

export const removeLog = defineEndpoint({ method: 'DELETE', path: '/ops/log/:id', permission: 'ops.manage', params: ById, response: Removed, idempotencyKey: 'required' });

// Haul, loadout and earnings (plan 2D Chunk 2). Each stages its change and publishes
// an event in the same commit; Ops' own handlers make the keyed calls.

const OpAndHaul = z.object({ op: Op, haul: z.array(HaulLine) });
const Stocking = z.object({ batch: z.string(), count: z.number().int(), left: z.number().int() });

export const addHaul = defineEndpoint({ method: 'POST', path: '/ops/ops/:id/haul', permission: 'ops.manage', params: ById, body: HaulInput, response: z.object({ lines: z.array(HaulLine) }), idempotencyKey: 'required' });
export const updateHaul = defineEndpoint({ method: 'PUT', path: '/ops/haul/:id', permission: 'ops.manage', params: ById, body: HaulUpdate, response: z.object({ line: HaulLine }), idempotencyKey: 'required' });
export const removeHaul = defineEndpoint({ method: 'DELETE', path: '/ops/haul/:id', permission: 'ops.manage', params: ById, response: Removed, idempotencyKey: 'required' });

/** Stamps the oldest pending lines with a batch (R7). */
export const stockHaul = defineEndpoint({ method: 'POST', path: '/ops/ops/:id/haul/stock', permission: 'ops.manage', params: ById, body: StockHaulInput, response: Stocking, idempotencyKey: 'required' });
/** Retries a failed batch, with the same key. */
export const retryHaul = defineEndpoint({ method: 'POST', path: '/ops/ops/:id/haul/retry', permission: 'ops.manage', params: ById, body: HaulBatchInput, response: OpAndHaul, idempotencyKey: 'required' });
/** Returns a failed batch's lines to Pending. */
export const cancelHaul = defineEndpoint({ method: 'POST', path: '/ops/ops/:id/haul/cancel', permission: 'ops.manage', params: ById, body: HaulBatchInput, response: OpAndHaul, idempotencyKey: 'required' });

export const changeLoadout = defineEndpoint({ method: 'POST', path: '/ops/ops/:id/loadout', permission: 'ops.manage', params: ById, body: LoadoutAction, response: OpDetail, idempotencyKey: 'required' });
export const deductLoadout = defineEndpoint({ method: 'POST', path: '/ops/ops/:id/loadout/deduct', permission: 'ops.manage', params: ById, response: z.object({ op: Op }), idempotencyKey: 'required' });
export const returnLoadout = defineEndpoint({ method: 'POST', path: '/ops/ops/:id/loadout/return', permission: 'ops.manage', params: ById, response: z.object({ op: Op }), idempotencyKey: 'required' });
export const retryLoadout = defineEndpoint({ method: 'POST', path: '/ops/ops/:id/loadout/retry', permission: 'ops.manage', params: ById, response: z.object({ op: Op }), idempotencyKey: 'required' });

/** The report's number. It stays editable after a deposit, which shows its own amount. */
export const setEarnings = defineEndpoint({ method: 'PUT', path: '/ops/ops/:id/earnings', permission: 'ops.manage', params: ById, body: EarningsInput, response: z.object({ op: Op }), idempotencyKey: 'required' });
export const depositEarnings = defineEndpoint({ method: 'POST', path: '/ops/ops/:id/deposit', permission: 'ops.deposit', params: ById, body: DepositInput, response: z.object({ op: Op }), idempotencyKey: 'required' });
export const reverseEarnings = defineEndpoint({ method: 'POST', path: '/ops/ops/:id/deposit/reverse', permission: 'ops.deposit', params: ById, body: ReverseInput, response: z.object({ op: Op }), idempotencyKey: 'required' });
export const retryDeposit = defineEndpoint({ method: 'POST', path: '/ops/ops/:id/deposit/retry', permission: 'ops.deposit', params: ById, response: z.object({ op: Op }), idempotencyKey: 'required' });
export const cancelDeposit = defineEndpoint({ method: 'POST', path: '/ops/ops/:id/deposit/cancel', permission: 'ops.deposit', params: ById, response: z.object({ op: Op }), idempotencyKey: 'required' });

const ShipChoice = z.object({ id: z.string(), name: z.string(), hull: z.string(), cargoCapacity: z.number().int().nullable(), used: z.number().int() });

/** What the sheets offer: accounts, places and the defaults, read through the bank's and supply's calls. */
export const getOpSheet = defineEndpoint({
  method: 'GET',
  path: '/ops/sheet',
  permission: 'ops.view',
  response: z.object({ accounts: z.array(z.object({ id: z.string(), name: z.string(), kind: z.enum(['primary', 'held']), holders: z.array(z.string()) })), locations: z.array(z.string()), ships: z.array(ShipChoice), defaultAccount: z.string().nullable() }),
});

/** Supply's items by part of a name, with their lots, for haul suggestions and the loadout picker. */
export const searchOpItems = defineEndpoint({
  method: 'GET',
  path: '/ops/items',
  permission: 'ops.view',
  query: z.object({ q: z.string().trim().max(80).default('') }),
  response: z.object({ items: z.array(Item), lots: z.array(Lot) }),
});

/** Active members by name, for a leader adding crew. */
export const searchOpMembers = defineEndpoint({
  method: 'GET',
  path: '/ops/members',
  permission: 'ops.manage',
  query: z.object({ q: z.string().trim().min(1).max(64) }),
  response: z.object({ people: z.array(z.object({ id: z.string(), displayName: z.string() })) }),
});

// Battle ops (plan 2D Chunk 4). Usage never changes stock.

export const changeShips = defineEndpoint({ method: 'POST', path: '/ops/ops/:id/ships', permission: 'ops.manage', params: ById, body: ShipsAction, response: z.object({ op: Op }), idempotencyKey: 'required' });
export const changeUsage = defineEndpoint({ method: 'POST', path: '/ops/ops/:id/usage', permission: 'ops.manage', params: ById, body: UsageAction, response: z.object({ op: Op }), idempotencyKey: 'required' });

/** The Used, last 30 days panel. */
export const getBattleTotals = defineEndpoint({ method: 'GET', path: '/ops/battle-totals', permission: 'ops.view', response: z.object({ totals: z.partialRecord(BattleResource, z.number().int()), ops: z.number().int() }) });

/** The Ops badge (R17): earnings waiting on the caller, from the same rule that notifies them. */
export const getOpCounts = defineEndpoint({ method: 'GET', path: '/ops/counts', permission: 'ops.deposit', response: z.object({ deposits: z.number().int() }) });

/** Makes the op for a pickup, or returns the one it already has (R15). */
export const planPickup = defineEndpoint({ method: 'POST', path: '/ops/pickups', permission: 'ops.manage', body: PickupInput, response: OpDetail, idempotencyKey: 'required' });

// People and the scorecard (plan 2D Chunk 7): worked out when read, through calls.

/** One row of People (R14). */
export const PersonRow = z.object({
  key: z.string(),
  user: z.string().nullable(),
  name: z.string(),
  profile: Profile.nullable(),
  ships: z.array(z.string()),
  ops30: z.number().int(),
  signed30: z.number().int(),
  opsAll: z.number().int(),
  donated: z.number().int(),
  donated30: z.number().int(),
  /** Plan 3B R7: ops they've time in voice on, and that time. Battle ops count here, though they're off the scorecard. */
  voiceOps30: z.number().int(),
  voiceSeconds30: z.number().int(),
  voiceOpsAll: z.number().int(),
  voiceSecondsAll: z.number().int(),
  lastOp: z.iso.datetime().nullable(),
  seen: z.iso.datetime().nullable(),
  /** A member who's lost access (Assumption 15). */
  left: z.boolean(),
});
export type PersonRow = z.infer<typeof PersonRow>;

const AttendanceRow = z.object({ key: z.string(), user: z.string().nullable(), name: z.string(), signed: z.number().int(), showed: z.number().int(), last: z.iso.datetime().nullable(), seen: z.iso.datetime().nullable() });

export const getPeople = defineEndpoint({ method: 'GET', path: '/ops/people', permission: 'ops.view_people', response: z.object({ items: z.array(PersonRow), attendance: z.array(AttendanceRow) }) });

const ScoreRow = z.object({ key: z.string(), user: z.string().nullable(), name: z.string(), ops: z.number().int(), haul: z.number().int(), donations: z.number().int(), donatedScu: z.number().int(), donatedAuec: z.number().int(), runs: z.number().int(), refinedScu: z.number().int(), total: z.number().int(), hidden: z.boolean() });

/** R13: what the weekly post draws, read through Ops so a leader needn't hold each module's permission. */
export const getWeekly = defineEndpoint({
  method: 'GET',
  path: '/ops/weekly',
  permission: 'ops.view_people',
  response: z.object({ schedule: z.array(OpSummary), requests: z.array(RequestView), people: z.record(z.string(), z.string()), ships: z.array(z.object({ id: z.string(), name: z.string() })), donors30: z.number().int(), score: z.array(ScoreRow) }),
});

/** R14: the monthly report's figures. */
export const getMonthly = defineEndpoint({
  method: 'GET',
  path: '/ops/monthly',
  permission: 'ops.view_people',
  response: z.object({ since: z.iso.datetime(), ops: z.number().int(), acquisition: z.number().int(), battle: z.number().int(), general: z.number().int(), haul: z.number().int(), donations: z.number().int(), refined: z.number().int(), shipsReady: z.number().int(), shipsTotal: z.number().int(), shipsShort: z.number().int(), openRequests: z.number().int(), top: z.array(ScoreRow) }),
});

/** R12's `member`: someone's all-time row and their last six ops. */
export const getMember = defineEndpoint({
  method: 'GET',
  path: '/ops/member',
  permission: 'ops.view_people',
  query: z.object({ key: z.string().max(128) }),
  response: z.object({ row: ScoreRow.nullable(), ops: z.array(z.object({ name: z.string(), start: z.string().nullable(), kind: OpKind, acqType: OpSummary.shape.acqType })) }),
});

export const getScorecard = defineEndpoint({ method: 'GET', path: '/ops/scorecard', permission: 'ops.view_people', query: z.object({ period: ScorePeriod.default('30') }), response: z.object({ period: ScorePeriod, items: z.array(ScoreRow) }) });

export const createProfile = defineEndpoint({ method: 'POST', path: '/ops/profiles', permission: 'ops.manage_people', body: ProfileInput, response: Profile, idempotencyKey: 'required' });
export const updateProfile = defineEndpoint({ method: 'PUT', path: '/ops/profiles/:id', permission: 'ops.manage_people', params: ById, body: ProfileUpdate, response: Profile, idempotencyKey: 'required' });

/** Removes only the notes: their history stays. */
export const deleteProfile = defineEndpoint({ method: 'DELETE', path: '/ops/profiles/:id', permission: 'ops.manage_people', params: ById, response: Removed, idempotencyKey: 'required' });

// Reports (plan 3C). A route holds one permission, and R2's rows are an author's or a
// manager's, so a report's own routes check `mayReport` inside, and refuse anyone with
// none of the three.

/** R4: a private draft, pre-filled from the op as it stands. */
export const createReport = defineEndpoint({ method: 'POST', path: '/ops/ops/:id/reports', permission: 'ops.report', params: ById, response: Report, idempotencyKey: 'required' });

/** The caller's own drafts. */
export const listReportDrafts = defineEndpoint({ method: 'GET', path: '/ops/report-drafts', permission: 'ops.report', response: z.object({ items: z.array(ReportSummary) }) });

/** A report as this reader may see it (R3). A draft that isn't theirs is a 404. */
export const getReport = defineEndpoint({ method: 'GET', path: '/ops/reports/:id', permission: 'authenticated', params: ById, response: Report });

/** Compare-and-set on the version. */
export const updateReport = defineEndpoint({ method: 'PUT', path: '/ops/reports/:id', permission: 'authenticated', params: ById, body: ReportUpdate, response: Report, idempotencyKey: 'required' });

/** Stores a screenshot for a report, unattached: the next save names it (plan 3D, F5). The body is the image. An author's or a manager's: see `mayReport`. */
export const uploadScreenshot = defineEndpoint({
  method: 'POST',
  path: '/ops/reports/:id/screenshots',
  permission: 'authenticated',
  params: ById,
  upload: { maxBytes: MAX_FILE_BYTES },
  rateLimit: { max: 20 },
  idempotencyKey: 'required',
  response: z.object({ file: RecordId }),
});

/** Drafts only: a published report is hidden instead. */
export const deleteReport = defineEndpoint({ method: 'DELETE', path: '/ops/reports/:id', permission: 'authenticated', params: ById, response: Removed, idempotencyKey: 'required' });

/** The archive, or one op's reports: what the caller may read, newest first, with no sections. */
export const listReports = defineEndpoint({ method: 'GET', path: '/ops/reports', permission: 'ops.view_reports', query: z.object({ op: RecordId.optional() }), response: z.object({ items: z.array(ReportSummary) }) });

/** Compare-and-set on the version. There's no going back to a draft, and version 1 is written with it (R9). */
export const publishReport = defineEndpoint({ method: 'POST', path: '/ops/reports/:id/publish', permission: 'authenticated', params: ById, body: PublishInput, response: Report, idempotencyKey: 'required' });

/** R7: restarts a stopped post. A part may already be on Discord, so the page warns first. */
export const retryReportPost = defineEndpoint({ method: 'POST', path: '/ops/reports/:id/post/retry', permission: 'authenticated', params: ById, response: Report, idempotencyKey: 'required' });

/** Takes it from readers, with a reason kept on the report and in the audit. It names one field, so it takes no version. */
export const hideReport = defineEndpoint({ method: 'POST', path: '/ops/reports/:id/hide', permission: 'ops.manage_reports', params: ById, body: HideInput, response: Report, idempotencyKey: 'required' });

export const unhideReport = defineEndpoint({ method: 'POST', path: '/ops/reports/:id/unhide', permission: 'ops.manage_reports', params: ById, response: Report, idempotencyKey: 'required' });

/** Every published version, newest first (R9). */
export const listReportVersions = defineEndpoint({ method: 'GET', path: '/ops/reports/:id/versions', permission: 'ops.manage_reports', params: ById, response: z.object({ items: z.array(ReportVersion) }) });

/** What each kind of op's reports start with. */
export const getReportTemplates = defineEndpoint({ method: 'GET', path: '/ops/report-templates', permission: 'ops.manage_reports', response: z.object({ items: z.array(ReportTemplate) }) });

/** Compare-and-set on the version, and 0 for a first save. Changes new drafts only. */
export const saveReportTemplate = defineEndpoint({ method: 'PUT', path: '/ops/report-templates/:kind', permission: 'ops.manage_reports', params: z.object({ kind: ReportKind }), body: TemplateInput, response: ReportTemplate, idempotencyKey: 'required' });
